import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import './styles/global.css';

import DashboardLayout from './components/layout/DashboardLayout';
import PublicLayout    from './components/layout/PublicLayout';
import CertificateManagement from './pages/admin/CertificateManagement';

const Login         = lazy(() => import('./pages/Login'));
const Home          = lazy(() => import('./pages/public/Home'));
const CoursesPage   = lazy(() => import('./pages/public/CoursesPage'));
const Apply         = lazy(() => import('./pages/public/Apply'));
const Placements    = lazy(() => import('./pages/public/PlacementsPage'));
const About         = lazy(() => import('./pages/public/About'));
const Contact       = lazy(() => import('./pages/public/Contact'));
const Gallery       = lazy(() => import('./pages/public/Gallery'));

const AdminDashboard       = lazy(() => import('./pages/admin/Dashboard'));
const AdminStudents        = lazy(() => import('./pages/admin/Students'));
const AdminTrainers        = lazy(() => import('./pages/admin/Trainers'));
const AdminCourses         = lazy(() => import('./pages/admin/Courses'));
const AdminBatches         = lazy(() => import('./pages/admin/Batches'));
const AdminTests           = lazy(() => import('./pages/admin/Tests'));
const AdminTestResults     = lazy(() => import('./pages/admin/TestResults'));
const AdminReports         = lazy(() => import('./pages/admin/Reports'));
const AdminPlacements      = lazy(() => import('./pages/admin/AdminPlacements'));
const AdminCMS             = lazy(() => import('./pages/admin/CMS'));
const AdminContactSubs     = lazy(() => import('./pages/admin/ContactSubmissions'));
const AdminAnalytics       = lazy(() => import('./pages/admin/Analytics'));
const AdminTrainerActivity = lazy(() => import('./pages/admin/TrainerActivity'));
const ProfilePage          = lazy(() => import('./pages/Profile'));
const CertificatePreview =lazy(() =>import('./pages/admin/CertificatePreview'));

const DirectorDashboard      = lazy(() => import('./pages/director/Dashboard'));
const DirectorSyllabus       = lazy(() => import('./pages/director/Syllabus'));
const DirectorUserManagement = lazy(() => import('./pages/admin/UserManagement'));
const DirectorLeaveApproval  = lazy(() => import('./pages/director/LeaveApproval'));
// Director uses same admin pages for shared content
const AdminFinance    = lazy(() => import('./pages/admin/Finance'));

const TrainerDashboard  = lazy(() => import('./pages/trainer/Dashboard'));
const TrainerAttendance = lazy(() => import('./pages/trainer/Attendance'));
const TrainerSyllabus   = lazy(() => import('./pages/trainer/Syllabus'));
const TrainerTests      = lazy(() => import('./pages/trainer/Tests'));
const TrainerStudents   = lazy(() => import('./pages/trainer/Students'));

const StudentDashboard = lazy(() => import('./pages/student/Dashboard'));
const StudentTest      = lazy(() => import('./pages/student/TakeTest'));
const StudentResult    = lazy(() => import('./pages/student/Result'));
const StudentTestResults = lazy(() => import('./pages/student/TestResults'));
const StudentSyllabus  = lazy(() => import('./pages/student/Syllabus'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [pathname]);
  return null;
}

const Loader = () => (
  <div style={{ display:'flex', justifyContent:'center', alignItems:'center', minHeight:'100vh' }}>
    <div className="spinner" />
  </div>
);

// Guard: waits for auth to initialize, then checks role
const Guard = ({ children, roles }) => {
  const { user, loading } = useAuth();
  // While auth is still initializing (e.g. /auth/me in flight), show spinner
  if (loading) return <Loader />;
  // Not logged in → go to login
  if (!user) return <Navigate to="/login" replace />;
  // Wrong role → access denied
  if (roles && !roles.includes(user.role)) return <Navigate to="/unauthorized" replace />;
  return children;
};

// After login, redirect to the correct dashboard for the role
const RoleRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  const map = {
    admin:       '/admin',
    director:    '/director',
    stakeholder: '/admin',
    trainer:     '/trainer',
    student:     '/student',
    placementOfficer: '/placement-officer',
  };
  return <Navigate to={map[user.role] || '/'} replace />;
};

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public pages */}
        <Route element={<PublicLayout />}>
          <Route path="/"           element={<Home />} />
          <Route path="/about"      element={<About />} />
          <Route path="/courses"    element={<CoursesPage />} />
          <Route path="/gallery"    element={<Gallery />} />
          <Route path="/placements" element={<Placements />} />
          <Route path="/contact"    element={<Contact />} />
          <Route path="/apply"      element={<Apply />} />
        </Route>
        <Route path="certificates/:certificateId" element={<CertificatePreview />}/>


        <Route path="/login"     element={<Login />} />
        <Route path="/dashboard" element={<RoleRedirect />} />
        <Route path="/unauthorized" element={
          <div style={{ textAlign:'center', padding:'80px 24px' }}>
            <div style={{ fontSize:'4rem', marginBottom:16 }}>🔒</div>
            <h2>Access Denied</h2>
            <p style={{ color:'#6B7280', marginTop:8 }}>You do not have permission to view this page.</p>
            <a href="/login" style={{ display:'inline-block', marginTop:20, padding:'10px 24px', background:'#4F46E5', color:'#fff', borderRadius:8, textDecoration:'none', fontSize:'.875rem' }}>
              Back to Login
            </a>
          </div>
        } />

        {/* ── Admin ─────────────────────────────────────────────────────────── */}
        {/* Admin does NOT have Finance (backend blocks admin on finance routes) */}
        <Route path="/admin" element={<Guard roles={['admin','stakeholder']}><DashboardLayout role="admin"/></Guard>}>
          <Route index                      element={<AdminDashboard />} />
          <Route path="students"            element={<AdminStudents />} />
          <Route path="trainers"            element={<AdminTrainers />} />
          <Route path="courses"             element={<AdminCourses />} />
          <Route path="batches"             element={<AdminBatches />} />
          <Route path="tests"               element={<AdminTests />} />
          <Route path="test-results"        element={<AdminTestResults />} />
          <Route path="reports"             element={<AdminReports />} />
          <Route path="placements"          element={<AdminPlacements />} />
          <Route path="cms"                 element={<AdminCMS />} />
          <Route path="contact-submissions" element={<AdminContactSubs />} />
          <Route path="analytics"           element={<AdminAnalytics />} />
          <Route path="trainer-activity"    element={<AdminTrainerActivity />} />
         <Route path="certificates"  element={<CertificateManagement />}/>
         <Route path="user-management" element={<DirectorUserManagement />}/>
          <Route path="attendance" element={<TrainerAttendance />} />
        </Route>

        {/* ── Director ──────────────────────────────────────────────────────── */}
        {/* Director has FULL access including Finance, Trainers, Syllabus, UserMgmt */}
        <Route path="/director" element={<Guard roles={['director']}><DashboardLayout role="director"/></Guard>}>
          <Route index                      element={<DirectorDashboard />} />
          <Route path="students"            element={<AdminStudents />} />
          <Route path="trainers"            element={<AdminTrainers />} />
          <Route path="courses"             element={<AdminCourses />} />
          <Route path="batches"             element={<AdminBatches />} />
          <Route path="tests"               element={<AdminTests />} />
          <Route path="test-results"        element={<AdminTestResults />} />
          <Route path="finance"             element={<AdminFinance />} />
          <Route path="reports"             element={<AdminReports />} />
          <Route path="placements"          element={<AdminPlacements />} />
          <Route path="cms"                 element={<AdminCMS />} />
          <Route path="contact-submissions" element={<AdminContactSubs />} />
          <Route path="analytics"           element={<AdminAnalytics />} />
          <Route path="trainer-activity"    element={<AdminTrainerActivity />} />
          <Route path="syllabus"            element={<DirectorSyllabus />} />
          <Route path="user-management"     element={<DirectorUserManagement />} />
          <Route path="leave-approvals"     element={<DirectorLeaveApproval />} />
          <Route path="certificates"element={<CertificateManagement />}/>

        </Route>

        {/* ── Trainer ───────────────────────────────────────────────────────── */}
        <Route path="/trainer" element={<Guard roles={['trainer']}><DashboardLayout role="trainer"/></Guard>}>
          <Route index             element={<TrainerDashboard />} />
          <Route path="attendance" element={<TrainerAttendance />} />
          <Route path="syllabus"   element={<TrainerSyllabus />} />
          <Route path="tests"      element={<TrainerTests />} />
          <Route path="students"   element={<TrainerStudents />} />
        </Route>
        {/* ── Placement Officer ───────────────────────────────────────────────────────── */}
        <Route path="/placement-officer" element={<Guard roles={['placementOfficer']}><DashboardLayout role="placementOfficer"/></Guard>}>
          <Route path="placements"          element={<AdminPlacements />} />
          <Route path="students"            element={<AdminStudents />} />
           <Route path="attendance" element={<TrainerAttendance />} />
        </Route>

        {/* ── Student ───────────────────────────────────────────────────────── */}
        <Route path="/student" element={<Guard roles={['student']}><DashboardLayout role="student"/></Guard>}>
          <Route index                 element={<StudentDashboard />} />
          <Route path="test/:testId"   element={<StudentTest />} />
          <Route path="result/:testId" element={<StudentResult />} />
          <Route path="test-results"   element={<StudentTestResults />} />
          <Route path="syllabus"       element={<StudentSyllabus />} />
        </Route>

        {/* Profile - all authenticated users */}
        <Route path="/profile" element={<Guard roles={['admin','director','trainer','student','stakeholder', 'placementOfficer']}><DashboardLayout role={null}/></Guard>}>
          <Route index element={<ProfilePage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration:3500, style:{ borderRadius:'10px', fontFamily:'DM Sans' } }} containerStyle={{ zIndex: 99999 }} />
        <Suspense fallback={<Loader />}>
          <AppRoutes />
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
